import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Heart, ArrowLeft, Trash2, Download, Loader2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { API_BASE } from "@/lib/api";

export default function KundliMilanHistory() {
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(null);
  const nav = useNavigate();

  const load = async () => {
    const { data } = await api.get("/kundli/milan/history");
    setItems(data);
  };
  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    if (!window.confirm("Delete this match record?")) return;
    await api.delete(`/kundli/milan/history/${id}`);
    setItems(items.filter(i => i.id !== id));
    toast.success("Deleted");
  };

  const downloadPdf = async (rec) => {
    setBusy(rec.id);
    try {
      const t = localStorage.getItem("access_token");
      const res = await fetch(`${API_BASE}/kundli/milan/pdf`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...(t ? { Authorization: `Bearer ${t}` } : {}) },
        credentials: "include",
        body: JSON.stringify({ record_id: rec.id }),
      });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${(rec.result.boy.name || "Groom")}_${(rec.result.girl.name || "Bride")}_Milan.pdf`.replace(/\s+/g, "_");
      a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("PDF failed"); }
    setBusy(null);
  };

  return (
    <div className="space-y-6" data-testid="milan-history-page">
      <div className="flex items-center gap-3">
        <Button variant="ghost" onClick={() => nav("/kundli-milan")} data-testid="milan-history-back"><ArrowLeft className="w-4 h-4 mr-1" /> Back</Button>
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-amber-600 dark:text-amber-400">Ashtakoot</div>
          <h1 className="font-display text-3xl md:text-4xl font-light tracking-tight mt-1 flex items-center gap-2">
            <Heart className="w-6 h-6 text-rose-500" /> Milan History
          </h1>
        </div>
      </div>

      {items.length === 0 ? (
        <Card className="p-12 rounded-2xl text-center border-dashed">
          <Heart className="w-10 h-10 mx-auto text-slate-400" strokeWidth={1.25} />
          <p className="mt-3 text-slate-500">No saved matches yet.</p>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map(rec => {
            const r = rec.result;
            const pct = (r.total / r.max) * 100;
            const col = pct >= 78 ? "emerald" : pct >= 66 ? "amber" : "red";
            return (
              <Card key={rec.id} className="p-5 rounded-2xl card-lift border-slate-200 dark:border-slate-800" data-testid={`milan-record-${rec.id}`}>
                <div className="flex items-start justify-between">
                  <div>
                    <div className="text-[10px] font-mono text-slate-500">{(rec.created_at || "").slice(0,10)}{rec.serial_no && <span className="ml-2 text-amber-700" data-testid={`milan-record-serial-${rec.id}`}>{rec.serial_no}</span>}</div>
                    <div className="font-display text-lg mt-1">{r.boy.name || "Groom"} <span className="text-slate-400">×</span> {r.girl.name || "Bride"}</div>
                    <div className="text-xs text-slate-500 mt-0.5">{r.boy.rashi} × {r.girl.rashi}</div>
                  </div>
                  <div className={`text-right`}>
                    <div className={`font-display text-3xl ${col === "emerald" ? "text-emerald-600" : col === "amber" ? "text-amber-600" : "text-red-600"}`}>{r.total}</div>
                    <div className="text-[10px] text-slate-500">/ {r.max}</div>
                  </div>
                </div>
                <Badge className={`mt-3 ${col === "emerald" ? "bg-emerald-100 text-emerald-800" : col === "amber" ? "bg-amber-100 text-amber-800" : "bg-red-100 text-red-800"}`}>{r.verdict}</Badge>
                <div className="mt-3 pt-3 border-t border-slate-100 dark:border-slate-800 flex items-center justify-end gap-1">
                  <Button size="sm" variant="ghost" onClick={() => downloadPdf(rec)} disabled={busy === rec.id} data-testid={`milan-record-pdf-${rec.id}`}>
                    {busy === rec.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Download className="w-3 h-3" />}
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => remove(rec.id)} className="text-red-600" data-testid={`milan-record-delete-${rec.id}`}>
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
